import { apiRequest } from '@utils/apiUtils'
import type { ApiResponse, PaginatedResponse, Timesheet } from '@/types'

// 審核統計數據類型
export interface ApprovalStats {
  totalHours: number
  totalAmount: number
  pendingCount: number
  overdueCount: number
  totalSubmissions: number
  completionRate: number
}

// 審核詳情數據類型
export interface ApprovalDetail extends Timesheet {
  totalAmount: number
  workingDays: number
  projectCount: number
  dailyHours: Record<string, number>
  projectHours: Record<string, { name: string, hours: number, color: string }>
  entriesByDate: Record<string, any[]>
}

// 分組的工時申報數據類型
export interface GroupedTimesheets {
  grouped: Record<string, Timesheet[]>
  timesheets: Timesheet[]
}

// 審核操作數據類型
export interface ApprovalAction {
  action: 'approve' | 'reject'
  comments?: string
  qualityScore?: number
  rejectionReason?: string
}

class ApprovalService {
  // 獲取審核統計
  async getApprovalStats(period: string = 'lastWeek'): Promise<ApiResponse<ApprovalStats>> {
    return apiRequest(`/api/timesheets/admin/stats?period=${period}`)
  }

  // 獲取待審核列表（分組）
  async getPendingApprovals(params: {
    page?: number
    limit?: number
    status?: string
    userId?: string
    search?: string
    groupBy?: string
  } = {}): Promise<ApiResponse<GroupedTimesheets & PaginatedResponse<Timesheet>>> {
    const queryParams = new URLSearchParams()
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        queryParams.append(key, value.toString())
      }
    })

    return apiRequest(`/api/timesheets/admin/pending?${queryParams.toString()}`)
  }

  // 獲取審核詳情
  async getApprovalDetail(timesheetId: string): Promise<ApiResponse<ApprovalDetail>> {
    return apiRequest(`/api/timesheets/admin/${timesheetId}/detail`)
  }

  // 批准工時申報
  async approveTimesheet(
    timesheetId: string, 
    data: { comments?: string, qualityScore?: number }
  ): Promise<ApiResponse<Timesheet>> {
    return apiRequest(`/api/timesheets/${timesheetId}/review`, {
      method: 'POST',
      body: JSON.stringify({
        action: 'approve',
        ...data
      })
    })
  }

  // 駁回工時申報
  async rejectTimesheet(
    timesheetId: string, 
    data: { rejectionReason: string }
  ): Promise<ApiResponse<Timesheet>> {
    return apiRequest(`/api/timesheets/${timesheetId}/review`, {
      method: 'POST',
      body: JSON.stringify({
        action: 'reject',
        comments: data.rejectionReason
      })
    })
  }

  // 刪除工時申報（管理員專用）
  async deleteTimesheet(timesheetId: string): Promise<ApiResponse<any>> {
    return apiRequest(`/api/timesheets/${timesheetId}/admin`, {
      method: 'DELETE'
    })
  }

  // 獲取所有用戶列表（用於篩選）
  async getUsers(): Promise<ApiResponse<{ id: string, name: string, email: string }[]>> {
    return apiRequest('/api/users?limit=100&isActive=true')
  }
}

export const approvalService = new ApprovalService()
export default approvalService
